import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';
import { formatViews } from '@/lib/movies';

export interface HeroSlideItem {
  movie: Movie;
  banner: string;
}

interface HeroCarouselProps {
  slides: HeroSlideItem[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = slides.length;

  const go = useCallback(
    (next: number) => {
      setIndex((next + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const active = slides[index].movie;

  return (
    <section className="relative w-full overflow-hidden">
      <div className="relative h-[420px] w-full md:h-[560px]">
        {slides.map((slide, i) => (
          <div
            key={slide.movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'opacity-0'
            }`}
            aria-hidden={i !== index}
          >
            <img
              src={slide.banner}
              alt={`${slide.movie.title} 焦点推荐`}
              title={`${slide.movie.title} 影视推荐`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/85 via-background-950/60 to-background-950/20"></div>

        <div className="relative flex h-full w-full items-center">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
                <i className="ri-hotspot-line"></i>
                今日焦点推荐
              </span>
              <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
                {active.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
                <span>{active.type}</span>
                <span className="text-background-400">|</span>
                <span>{active.genre}</span>
                <span className="text-background-400">|</span>
                <span>{active.region}</span>
                <span className="text-background-400">|</span>
                <span>{active.year}</span>
                <span className="flex items-center gap-1 text-accent-300">
                  <i className="ri-fire-line"></i>
                  {formatViews(active.views)} 热度
                </span>
              </div>
              <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
                {active.summary}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${active.id}`}
                  className="flex items-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  查看详情
                </Link>
                <Link
                  to="/category"
                  className="flex items-center gap-2 rounded-md border border-background-100/40 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10 whitespace-nowrap"
                >
                  <i className="ri-compass-3-line text-lg"></i>
                  浏览全部
                </Link>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一个"
          className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-950/40 text-background-50 transition-colors hover:bg-background-950/70 md:left-6 cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一个"
          className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-950/40 text-background-50 transition-colors hover:bg-background-950/70 md:right-6 cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {slides.map((slide, i) => (
            <button
              key={slide.movie.id}
              type="button"
              aria-label={`切换到 ${slide.movie.title}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}